import React, { useMemo } from 'react';
import { SystemCard } from '../components/SystemCard';
import { SystemCardPopoverContent } from '../components/SystemCardPopoverContent';
import { PlugLoadWithMetrics } from '../types';
import { iconForEnergySink, isPassiveTechnology } from '../utils';

const PLUGLOAD_SYSTEM_IMAGE = (
  <svg
    width='61'
    height='61'
    viewBox='0 0 61 61'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M21.0897 7.79688H27.0357V16.4455H32.9816V7.79688H38.9275V16.4455C41.9129 16.4455 44.3329 18.8656 44.3329 21.8509V41.8509C44.3329 44.8363 41.9129 47.2563 38.9275 47.2563H32.9816V60.4996H27.0357V47.2563H22.1708C19.1855 47.2563 16.7654 44.8363 16.7654 41.8509V21.8509C16.7654 19.2358 18.6224 17.0545 21.0897 16.5537V7.79688ZM28.1801 44.6439L30.551 34.0443H24.0072L32.2581 19.4169L30.0768 29.1685H36.6207L28.1801 44.6439Z'
      fill='currentColor'
    />
  </svg>
);

type PlugLoadUnitProps = {
  item: PlugLoadWithMetrics;
  selected: boolean;
  count?: number;
  isLoading?: boolean;
  readOnly?: boolean;
  actions: {
    onAdd?: () => void;
    onIncrement?: () => void;
    onDecrement?: () => void;
    onRemove?: () => void;
    onChangeCount?: (newCount: number) => void;
  };
};

export const PlugLoadUnit: React.FC<PlugLoadUnitProps> = React.memo(
  ({ item, selected, count, isLoading, actions, readOnly }) => {
    const EnergyIcon = iconForEnergySink(item.energySink);

    const icons = useMemo(
      () => ({
        EnergyIcon: EnergyIcon,
        energyIconCount: item.relativeEnergyDemand,
        costIconCount: item.relativeCost,
      }),
      [EnergyIcon, item.relativeEnergyDemand, item.relativeCost],
    );

    const hoverPopover = useMemo(
      () => (
        <SystemCardPopoverContent
          name={item.name}
          energySource={item.energySink}
          EnergySourceIcon={EnergyIcon}
          limit={item.limit}
          coveragePercentage={item.coveragePercentage}
          energyDemand={Number(item.energyUsePerYr)}
          capitalCost={Number(item.capitalCostPerUnit)}
          annualCost={Number(item.maintenanceCostPerUnit)}
          coverageFieldTitle={'plug load'}
          designLife={item.designLife}
          isLoading={isLoading}
        />
      ),
      [item, EnergyIcon, isLoading],
    );

    return (
      <SystemCard
        actions={actions}
        count={count}
        limit={item.limit}
        name={item.name}
        selected={selected}
        systemImage={PLUGLOAD_SYSTEM_IMAGE}
        icons={icons}
        hoverPopover={hoverPopover}
        readOnly={readOnly}
        unitsFixed={isPassiveTechnology(item)}
      />
    );
  },
);

PlugLoadUnit.displayName = 'PlugLoadUnit';
